import { useState } from 'react';
import { useNavigate } from 'react-router';
import clsx from 'clsx';

import { Button, Card, Icon } from '@lib/ui';
import { Input, Textarea, Select, Switch } from '@lib/ui';
import type { FlexibleSource, FlexibleInput } from '../types';
import { defaultFlexibleSource, LEGACY_SCENARIOS } from '../types';
import { SourcePreview } from '../components/SourcePreview';

export default function FlexibleWizard() {
  const navigate = useNavigate();
  const [source, setSource] = useState<FlexibleSource>(defaultFlexibleSource);
  const [activeScenario, setActiveScenario] = useState<string | null>(null);

  const updateSource = (updates: Partial<FlexibleSource>) => {
    setSource((prev) => ({ ...prev, ...updates }));
  };

  const addInput = () => {
    updateSource({
      inputs: [...source.inputs, { name: '', type: 'string', required: true }],
    });
  };

  const updateInput = (index: number, updates: Partial<FlexibleInput>) => {
    const newInputs = [...source.inputs];
    newInputs[index] = { ...newInputs[index], ...updates };
    updateSource({ inputs: newInputs });
  };

  const removeInput = (index: number) => {
    updateSource({ inputs: source.inputs.filter((_, i) => i !== index) });
  };

  const addTool = (type: 'web_fetch' | 'web_discover') => {
    const config: Record<string, string> = type === 'web_fetch' ? { url: '' } : { baseUrl: '' };
    updateSource({
      tools: [...source.tools, { type, config }],
    });
  };

  const updateTool = (index: number, config: Record<string, string>) => {
    const newTools = [...source.tools];
    newTools[index] = { ...newTools[index], config };
    updateSource({ tools: newTools });
  };

  const removeTool = (index: number) => {
    updateSource({ tools: source.tools.filter((_, i) => i !== index) });
  };

  const loadScenario = (scenarioId: string) => {
    setActiveScenario(scenarioId);
    switch (scenarioId) {
      case 'pure-llm':
        setSource({
          title: 'Daily Motivation',
          prompt: 'Write a motivational quote about perseverance',
          inputs: [],
          tools: [],
        });
        break;
      case 'url-summary':
        setSource({
          title: 'Article Summarizer',
          prompt: `Article content:
{{web_fetch.content}}

Summarize the main points in 3 bullet points.`,
          inputs: [{ name: 'url', type: 'url', required: true, description: 'URL to summarize' }],
          tools: [{ type: 'web_fetch', config: { url: '{{inputs.url}}' } }],
        });
        break;
      case 'site-discovery':
        setSource({
          title: 'Site Pricing Extractor',
          prompt: `Pages found:
{{web_discover.pages}}

Extract all pricing tiers with their features.`,
          inputs: [{ name: 'site', type: 'url', required: true, description: 'Site to analyze' }],
          tools: [
            { type: 'web_discover', config: { baseUrl: '{{inputs.site}}', hint: 'pricing' } },
          ],
        });
        break;
      case 'complex-extraction':
        setSource({
          title: 'Amazon Product Reviews',
          prompt: `Product page content:
{{web_fetch.content}}

Extract all user comments. For each:
- Username
- Rating (1-5)
- Date
- Comment text

Then summarize overall sentiment.`,
          inputs: [],
          tools: [{ type: 'web_fetch', config: { url: 'https://amazon.com/product/123' } }],
        });
        break;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-900">
      {/* Header */}
      <div className="bg-white dark:bg-dark-800 border-b border-gray-200 dark:border-dark-600 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-blue-500 w-10 h-10 rounded-lg flex items-center justify-center">
              <Icon name="folder" className="text-white" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                Flexible Model
              </h1>
              <p className="text-sm text-gray-500 dark:text-dark-400">
                Dynamic inputs with tool array
              </p>
            </div>
          </div>
          <Button variant="flat" onClick={() => navigate('/poc/source-modeling')}>
            Back
          </Button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Scenario selector */}
        <div className="mb-6">
          <p className="text-sm font-medium text-gray-700 dark:text-dark-200 mb-2">
            Load a scenario:
          </p>
          <div className="flex flex-wrap gap-2">
            {LEGACY_SCENARIOS.map((scenario) => (
              <button
                key={scenario.id}
                onClick={() => loadScenario(scenario.id)}
                className={clsx(
                  'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                  activeScenario === scenario.id
                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                    : 'bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-dark-300 hover:bg-gray-200 dark:hover:bg-dark-600'
                )}
              >
                {scenario.name}
              </button>
            ))}
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Form */}
          <Card skin="shadow" className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
              Configure Source
            </h2>

            <div className="space-y-6">
              {/* Title */}
              <Input
                label="Title"
                placeholder="e.g., Article Summarizer"
                value={source.title}
                onChange={(e) => updateSource({ title: e.target.value })}
              />

              {/* Inputs */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium text-fg">Dynamic Inputs</label>
                  <Button size="sm" variant="soft" onClick={addInput}>
                    <Icon name="plus" size="sm" />
                    <span className="ml-1">Add Input</span>
                  </Button>
                </div>
                {source.inputs.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-dark-400 italic">
                    No dynamic inputs. Values will be static.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {source.inputs.map((input, index) => (
                      <div
                        key={index}
                        className="p-3 rounded-lg border border-gray-200 dark:border-dark-600 space-y-3"
                      >
                        <div className="flex items-start gap-3">
                          <Input
                            placeholder="Input name"
                            value={input.name}
                            onChange={(e) => updateInput(index, { name: e.target.value })}
                            classNames={{ root: 'flex-1' }}
                          />
                          <Select
                            value={input.type}
                            onChange={(e) =>
                              updateInput(index, { type: e.target.value as FlexibleInput['type'] })
                            }
                            data={['string', 'url', 'text']}
                            classNames={{ root: 'w-28' }}
                          />
                          <Button
                            isIcon
                            variant="flat"
                            color="error"
                            size="sm"
                            onClick={() => removeInput(index)}
                          >
                            <Icon name="x" size="sm" />
                          </Button>
                        </div>
                        <div className="flex items-center gap-3">
                          <Switch
                            checked={input.required}
                            onChange={(e) => updateInput(index, { required: e.target.checked })}
                            size="sm"
                            label="Required"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Tools */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium text-fg">Tools</label>
                  <div className="flex gap-2">
                    <Button size="sm" variant="soft" onClick={() => addTool('web_fetch')}>
                      <Icon name="plus" size="sm" />
                      <span className="ml-1">Web Fetch</span>
                    </Button>
                    <Button size="sm" variant="soft" onClick={() => addTool('web_discover')}>
                      <Icon name="plus" size="sm" />
                      <span className="ml-1">Web Discover</span>
                    </Button>
                  </div>
                </div>
                {source.tools.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-dark-400 italic">
                    No tools. Pure LLM generation.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {source.tools.map((tool, index) => (
                      <div
                        key={index}
                        className="p-3 rounded-lg border border-gray-200 dark:border-dark-600"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {tool.type === 'web_fetch' ? 'Web Fetch' : 'Web Discover'}
                          </span>
                          <Button
                            isIcon
                            variant="flat"
                            color="error"
                            size="sm"
                            onClick={() => removeTool(index)}
                          >
                            <Icon name="x" size="sm" />
                          </Button>
                        </div>
                        {tool.type === 'web_fetch' ? (
                          <Input
                            label="URL"
                            placeholder="https://... or {{inputs.url}}"
                            value={tool.config.url || ''}
                            onChange={(e) => updateTool(index, { url: e.target.value })}
                            description="Use {{inputs.name}} for dynamic values"
                          />
                        ) : (
                          <div className="space-y-3">
                            <Input
                              label="Base URL"
                              placeholder="https://... or {{inputs.site}}"
                              value={tool.config.baseUrl || ''}
                              onChange={(e) =>
                                updateTool(index, { ...tool.config, baseUrl: e.target.value })
                              }
                            />
                            <Input
                              label="Hint (optional)"
                              placeholder="e.g., pricing"
                              value={tool.config.hint || ''}
                              onChange={(e) =>
                                updateTool(index, { ...tool.config, hint: e.target.value })
                              }
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Prompt */}
              <Textarea
                label="Prompt Template"
                placeholder={`Use {{tool_name.content}} to reference tool output.
Example:
{{web_fetch.content}}

Summarize the main points.`}
                rows={6}
                value={source.prompt}
                onChange={(e) => updateSource({ prompt: e.target.value })}
                description="Use {{web_fetch.content}} or {{web_discover.pages}} to inject tool results"
              />
            </div>
          </Card>

          {/* Preview */}
          <div className="space-y-6">
            <SourcePreview title="Data Structure" data={source} format="json" />
            <SourcePreview title="User Config (YAML)" data={source} format="yaml" />
          </div>
        </div>
      </div>
    </div>
  );
}
